// src/modules/app-views/products/pages/Document/modals/ProductsParametersModal/tabs/UnitsTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo } from 'react';
import { Table, Button, Flex, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import { generateUidV4 } from 'utils/helpers';
import useZustand from 'hooks/useZustand';
import UnitFormModal from './UnitFormModal';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const UnitsTab = ({ loading, setLoading, moduleId: moduleIdProp }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedModule = useZustand('module', 'config');
  // Allow overriding moduleId for shared usage (e.g., goodsReceipt reusing products tabs)
  const moduleId = moduleIdProp || storedModule?.id || 'products';

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [mousePosition, setMousePosition] = useState(null);

  // Use local state for units - initialize from store, update locally after save
  const [units, setUnits] = useState(() => {
    const rawUnits = appService.getModulePreferences({ moduleId, category: 'units' });
    return normalizeUnits(rawUnits);
  });

  // Filter out removed units for display in table
  const visibleUnits = useMemo(() => units.filter(u => !u.removed), [units]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAdd = useCallback(e => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(null);
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((record, e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(record);
    setShowModal(true);
  }, []);

  const handleSave = useCallback(
    async formData => {
      // Check for duplicates (excluding current item if editing, and excluding removed units)
      const isDuplicate = units.some(
        u =>
          u.uid !== editingRecord?.uid &&
          !u.removed &&
          u.name.toLowerCase() === formData.name.toLowerCase()
      );

      if (isDuplicate) {
        appService.message({ flag: 'w', text: 'item_already_exists' });
        return;
      }

      setLoading(true);
      try {
        let updatedUnits;

        if (editingRecord) {
          // Editing existing unit - keep the same uid and preserve removed flag
          updatedUnits = units.map(u =>
            u.uid === editingRecord.uid
              ? { ...u, name: formData.name, description: formData.description }
              : u
          );
        } else {
          // Adding new unit - generate new uid
          updatedUnits = [
            ...units,
            {
              uid: generateUidV4(),
              name: formData.name,
              description: formData.description,
            },
          ];
        }

        // Save array directly: units: [...]
        await appService.updateModulePreferences({
          moduleId,
          category: 'units',
          data: updatedUnits,
        });

        // Update local state immediately (don't wait for SSE)
        setUnits(updatedUnits);

        appService.message({ flag: 's', text: 'saved_successfully' });
        setShowModal(false);
        setEditingRecord(null);
      } catch (error) {
        console.error('[UnitsTab] Save error:', error);
        appService.message({ flag: 'e', text: 'error_saving' });
      } finally {
        setLoading(false);
      }
    },
    [units, editingRecord, setLoading]
  );

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setEditingRecord(null);
  }, []);

  const handleDelete = useCallback(
    async record => {
      setLoading(true);
      try {
        // Check if unit is in use via API
        const isInUse = await appService.checkParameterInUse({
          module: moduleId,
          category: 'units',
          uid: record.uid,
        });

        let updatedUnits;

        if (isInUse) {
          // Soft delete: mark as removed but keep in array
          updatedUnits = units.map(u =>
            u.uid === record.uid ? { ...u, removed: true } : u
          );
          appService.message({ flag: 'w', text: 'item_in_use_hidden' });
        } else {
          // Hard delete: remove from array
          updatedUnits = units.filter(u => u.uid !== record.uid);
          appService.message({ flag: 's', text: 'removed_successfully' });
        }

        // Save array directly: units: [...]
        await appService.updateModulePreferences({
          moduleId,
          category: 'units',
          data: updatedUnits,
        });

        // Update local state immediately (don't wait for SSE)
        setUnits(updatedUnits);
      } catch (error) {
        console.error('[UnitsTab] Delete error:', error);
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [units, setLoading, storedModule?.id]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. TABLE COLUMNS
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: translateX('name'),
      dataIndex: 'name',
      width: 120,
      render: text => <span className="text-uppercase font-weight-bold">{text}</span>,
    },
    {
      title: translateX('description'),
      dataIndex: 'description',
      render: text => text || <span className="text-muted">-</span>,
    },
    {
      title: translateX('actions'),
      dataIndex: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={e => handleEdit(record, e)}
            disabled={loading}
          />
          <Popconfirm
            title={translateX('confirm_delete')}
            onConfirm={() => handleDelete(record)}
            okText={translateX('yes')}
            cancelText={translateX('no')}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={loading} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Flex vertical style={{ height: '100%' }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table
          rowKey="uid"
          columns={columns}
          dataSource={visibleUnits}
          pagination={false}
          size="small"
          loading={loading}
          locale={{ emptyText: translateX('no_data') }}
          scroll={{ y: 'calc(80vh - 240px)' }}
        />
      </div>

      <Flex justify="flex-end" className="mt-3" style={{ flexShrink: 0 }}>
        <Button icon={<PlusOutlined />} onClick={handleAdd} disabled={loading}>
          {translateX('add_unit')}
        </Button>
      </Flex>

      <UnitFormModal
        open={showModal}
        onCancel={handleCancel}
        onSave={handleSave}
        initialData={editingRecord}
        loading={loading}
        mousePosition={mousePosition}
      />
    </Flex>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default UnitsTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function normalizeUnits(rawData) {
  if (!rawData || !Array.isArray(rawData)) return [];
  return rawData.map(normalizeUnit);
}

function normalizeUnit(unit) {
  return {
    uid: unit.uid,
    name: unit.name || '',
    description: unit.description || '',
    ...(unit.removed && { removed: true }),
  };
}
